import {
	Button,
	Card,
	Dialog,
	Input,
	Loader,
	SettingsForm,
	Text,
	tailwindPreset,
} from "@southfields-digital/mxmz-react-ui";
import { useContext, useEffect, useState } from "react";
import { type NavigateFunction, useNavigate } from "react-router-dom";

import ResolutionPicker, { PRESET_RESOLUTIONS } from "src/components/ResolutionPicker";
import { BaseLayoutContext, type BaseLayoutContextType } from "src/layouts/BaseLayout";
import menuGroups from "src/pages/Settings/menuGroups";
import { isValidResolution } from "src/utils";

import styles from "./CanvasOverviewPage.module.scss";

type CanvasOverviewPageProps = {
	deleteCanvas: (id: string) => void;
	getCanvases: () => void;
	loading: boolean;
	canvasData: Canvas[];
	submitting: boolean;
	updateCanvas: (
		id: string,
		name: string,
		width: number,
		height: number,
		navigate?: NavigateFunction,
	) => void;
};

export default function CanvasOverviewPage({
	deleteCanvas,
	getCanvases,
	loading,
	canvasData,
	submitting,
	updateCanvas,
}: CanvasOverviewPageProps) {
	const navigate = useNavigate();
	const [deletingCanvas, setDeletingCanvas] = useState<Canvas | null>(null);
	const [editingCanvas, setEditingCanvas] = useState<Canvas | null>(null);
	const { setWithContainer } = useContext<BaseLayoutContextType | null>(
		BaseLayoutContext,
	) as BaseLayoutContextType;

	const canvasName = editingCanvas?.name?.trim();
	const canUpdate =
		!!canvasName?.length &&
		!submitting &&
		isValidResolution({ width: editingCanvas?.width ?? 0, height: editingCanvas?.height ?? 0 });

	const handleUpdateCanvas = (canvas: Canvas) => {
		if (!editingCanvas || !canUpdate) return;
		updateCanvas(canvas.id, editingCanvas.name.trim(), editingCanvas.width, editingCanvas.height);
		setEditingCanvas(null);
	};

	useEffect(() => {
		const resetEditingCanvasOnEscape = (e: KeyboardEvent) => {
			if (e.key === "Escape") {
				setEditingCanvas(null);
			}
		};

		setWithContainer(true);
		getCanvases();

		document.addEventListener("keydown", resetEditingCanvasOnEscape, true);

		return function unmount() {
			setWithContainer(false);
			// Clean up edit/delete state to prevent stale UI
			setEditingCanvas(null);
			setDeletingCanvas(null);

			document.removeEventListener("keydown", resetEditingCanvasOnEscape, true);
		};
	}, []);

	const handleClickDelete = (canvas: Canvas) => {
		setDeletingCanvas(canvas);
	};

	return (
		<>
			<SettingsForm
				title="Manage canvases"
				formSections={[
					{
						type: "overview",
						onCreate: () => navigate("/settings/workspace/canvases/create"),
						onCreateLabel: "Add canvas",
						overview: (
							<div className={styles.CanvasOverview}>
								{(loading || submitting) && (
									<div className={styles.LoaderWrapper}>
										<Loader className={styles.Loader} />
									</div>
								)}

								{canvasData?.map((canvas, index) => (
									<Card
										key={index}
										className={styles.Canvas}
										actionItems={[
											{
												as: "button",
												onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
													e.preventDefault();
													e.stopPropagation();
													setEditingCanvas(canvas);
												},
												value: "Edit",
											},
											{
												as: "button",
												onClick: (e: React.MouseEvent<HTMLButtonElement>) => {
													e.preventDefault();
													e.stopPropagation();
													handleClickDelete(canvas);
												},
												value: "Delete",
											},
										]}
										headerContent={
											editingCanvas?.id !== canvas.id ? (
												<Text className={styles.CanvasCardTitle} as="h3">
													<span onClick={() => setEditingCanvas(canvas)}>{canvas.name}</span>
												</Text>
											) : (
												<Input
													className={styles.CanvasCardTitle}
													type="text"
													value={editingCanvas?.name}
													onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
														setEditingCanvas((prev) =>
															prev ? { ...prev, name: e.target.value } : prev,
														);
													}}
													onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
														if (e.key === "Enter") {
															e.preventDefault();
															handleUpdateCanvas(canvas);
														}
													}}
													placeholder="Pick a recognisable name for your canvas"
													suffix={
														<Button
															className={styles.UpdateCanvasButton}
															iconProps={{
																icon: "check",
																color: tailwindPreset.theme.extend.colors.primary,
																weight: "bold",
															}}
															onClick={() => handleUpdateCanvas(canvas)}
															variant="tertiary"
															disabled={!canUpdate}
														/>
													}
												/>
											)
										}
									>
										{editingCanvas?.id !== canvas.id ? (
											<div className="flex items-center gap-2">
												<Text as="span">Resolution</Text>
												<Text as="span" className={styles.CanvasResolution}>
													{canvas.width}×{canvas.height}
												</Text>
											</div>
										) : (
											<div className="flex flex-col gap-1">
												<Text as="span">Resolution</Text>
												<ResolutionPicker
													selectedPreset={
														PRESET_RESOLUTIONS.find(
															(r) =>
																r.width === editingCanvas.width &&
																r.height === editingCanvas.height,
														) ?? null
													}
													presets={PRESET_RESOLUTIONS}
													resolution={{ width: editingCanvas.width, height: editingCanvas.height }}
													onChange={(w, h) =>
														setEditingCanvas((prev) =>
															prev ? { ...prev, width: w, height: h } : prev,
														)
													}
												/>
											</div>
										)}
									</Card>
								))}
							</div>
						),
					},
				]}
				menuGroups={menuGroups}
			/>
			<Dialog backdrop open={Boolean(deletingCanvas)} onClose={() => setDeletingCanvas(null)}>
				<Dialog.Panel className="flex flex-col z-50">
					<Dialog.Title>Deleting canvas</Dialog.Title>
					<Text as="span">
						Are you sure you want to delete the canvas <strong>{deletingCanvas?.name}</strong>?
					</Text>
					<div className="flex flex-row w-full gap-2 pt-4">
						<Button onClick={() => setDeletingCanvas(null)} variant="secondary">
							Cancel
						</Button>
						<Button
							onClick={() => {
								if (!deletingCanvas) return;
								deleteCanvas(deletingCanvas.id);
								setDeletingCanvas(null);
							}}
							variant="danger"
						>
							Delete
						</Button>
					</div>
				</Dialog.Panel>
			</Dialog>
		</>
	);
}
